import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryResult,
  UseMutationResult
} from "react-query";
import { useDispatch } from "react-redux";

import { activeIpdsQueryKey } from "./useIpds";
import { notificationAdd } from "../../actions/notification";
import { bedsQueryKey, occupiedBedsQueryKey } from "./useBeds";
import useCurrentResourceCentre from "../useCurrentResourceCentre";
import { getBedAssignments, transferBed } from "../../api/bedAssignment";
import { BedAssignment, BedAssignmentFormValues } from "../../interfaces/BedAssignmentInterface";

export const bedAssignmentsQueryKey = "bedAssignments";

// use beds
export const useBedAssignments = (props?: {
  ipdId?: number;
  bedId?: number;
  active?: boolean;
}): UseQueryResult<Array<BedAssignment>, Error> => {
  const resourceCentre = useCurrentResourceCentre();

  return useQuery({
    queryKey: [
      bedAssignmentsQueryKey,
      resourceCentre?.id,
      props?.active,
      props?.ipdId,
      props?.bedId
    ],
    queryFn: async () => {
      if (!resourceCentre?.id) return [];

      const response = await getBedAssignments({ resourceCentreId: resourceCentre.id, ...props });
      return response.data || [];
    },
    staleTime: 1000 * 60 * 5,
    enabled: !!resourceCentre?.id
  });
};

// mutation
export const useTransferBed = (): UseMutationResult<
  BedAssignment,
  Error,
  BedAssignmentFormValues,
  unknown
> => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const resourceCentre = useCurrentResourceCentre();

  return useMutation({
    mutationFn: async (data: BedAssignmentFormValues) => {
      const response = await transferBed(data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [bedAssignmentsQueryKey, resourceCentre?.id] });
      queryClient.invalidateQueries({ queryKey: [bedsQueryKey, resourceCentre?.id] });
      queryClient.invalidateQueries({ queryKey: [occupiedBedsQueryKey, resourceCentre?.id] });
      queryClient.invalidateQueries({ queryKey: [activeIpdsQueryKey, resourceCentre?.id] });

      dispatch(
        notificationAdd({
          id: Date.now(),
          autoTimeout: true,
          message: "Bed(s) created successfully",
          variant: "success"
        })
      );
    },
    onError: (error: any) => {
      dispatch(
        notificationAdd({
          id: Date.now(),
          autoTimeout: true,
          message: error?.message || "Failed to create bed(s)",
          variant: "error"
        })
      );
    }
  });
};
